import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Compass,
  Home,
  ImageIcon,
  Printer,
  Square,
} from 'lucide-react';

// "Sticky price rail" layout: wide gallery and content on the left, while the
// price, status and enquiry button ride along in a sticky card on the right —
// the commercial details stay on screen for the whole scroll.

interface StickyApartment {
  id: string;
  number: string;
  floor: number;
  entrance: string;
  rooms: number;
  area: number;
  netArea?: number;
  price: number;
  promoPrice?: number;
  status: 'available' | 'reserved' | 'sold';
  description: string;
  features: string[];
  exposure: string;
  brochureUrl?: string;
  model3d?: { aiInteriors?: string[] };
}

interface Props {
  apartment: StickyApartment;
  images: string[];
  mainImage: string;
  entranceLabel: string;
  statusText: string;
  statusColor: string;
  exposureText: string;
  backPath: string;
  onImageClick: (index: number) => void;
  onContact: () => void;
}

const StickyDetailsLayout = ({
  apartment,
  images,
  mainImage,
  entranceLabel,
  statusText,
  statusColor,
  exposureText,
  backPath,
  onImageClick,
  onContact,
}: Props) => {
  const thumbnails = images.filter((img) => img !== mainImage);
  const aiInteriors = apartment.model3d?.aiInteriors || [];
  const hasPromo = !!apartment.promoPrice && apartment.promoPrice < apartment.price;

  const specs = [
    { icon: Building2, label: 'Етаж', value: String(apartment.floor) },
    { icon: Home, label: 'Стаи', value: String(apartment.rooms) },
    { icon: Square, label: 'Обща площ', value: `${apartment.area} м²` },
    ...(apartment.netArea
      ? [{ icon: Square, label: 'Чиста площ', value: `${apartment.netArea} м²` }]
      : []),
    { icon: Compass, label: 'Изложение', value: exposureText },
    { icon: Building2, label: 'Вход', value: entranceLabel },
  ];

  return (
    <div className="pt-24 pb-16 bg-neutral-50">
      <div className="container-custom">
        <Link
          to={backPath}
          className="inline-flex items-center text-primary hover:text-primary-dark mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Обратно към списъка
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: gallery + content */}
          <div className="lg:col-span-2 space-y-8">
            <div className="rounded-2xl overflow-hidden shadow-lg bg-white">
              <button
                type="button"
                onClick={() => onImageClick(0)}
                className="block w-full group"
              >
                <img
                  src={mainImage}
                  alt={apartment.number}
                  className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-300"
                />
              </button>
            </div>

            {thumbnails.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {thumbnails.map((image, index) => (
                  <button
                    key={image}
                    type="button"
                    onClick={() => onImageClick(index + 1)}
                    className="rounded-xl overflow-hidden shadow group aspect-[4/3] bg-white"
                  >
                    <img
                      src={image}
                      alt={`${apartment.number} — изглед ${index + 2}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </button>
                ))}
              </div>
            )}

            {apartment.description && (
              <section className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg">
                <h2 className="text-xl font-semibold mb-4 text-gray-900">Описание</h2>
                <p className="text-neutral-600 leading-relaxed whitespace-pre-line">
                  {apartment.description}
                </p>
              </section>
            )}

            {apartment.features && apartment.features.length > 0 && (
              <section className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg">
                <h2 className="text-xl font-semibold mb-4 text-gray-900">Характеристики</h2>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {apartment.features.map((feature, index) => (
                    <li key={index} className="flex items-start text-neutral-600">
                      <span className="w-2 h-2 bg-primary rounded-full mr-3 mt-2 flex-shrink-0" />
                      <span className="leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {aiInteriors.length > 0 && (
              <section className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg">
                <div className="flex items-center mb-6">
                  <ImageIcon className="w-6 h-6 text-primary mr-2" />
                  <h2 className="text-xl font-semibold text-gray-900">AI интериор</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {aiInteriors.map((url, i) => (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-xl overflow-hidden shadow group block"
                    >
                      <img
                        src={url}
                        alt={`AI интериор ${i + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </a>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-3">
                  Примерна визуализация на обзавеждането, генерирана с изкуствен интелект.
                </p>
              </section>
            )}
          </div>

          {/* Right: sticky commercial panel */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 space-y-4">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <h1 className="text-2xl font-bold text-gray-900">{apartment.number}</h1>
                  <span
                    className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap ${statusColor}`}
                  >
                    {statusText}
                  </span>
                </div>

                {apartment.price > 0 && (
                  <div className="mb-6">
                    <p className="text-sm text-neutral-500">Цена</p>
                    {hasPromo ? (
                      <div className="flex flex-wrap items-baseline gap-2 mt-1">
                        <span className="text-lg text-gray-400 line-through">
                          €{apartment.price.toLocaleString()}
                        </span>
                        <span className="text-3xl font-bold text-red-600">
                          €{apartment.promoPrice!.toLocaleString()}
                        </span>
                      </div>
                    ) : (
                      <p className="text-3xl font-bold text-gray-900 mt-1">
                        €{apartment.price.toLocaleString()}
                      </p>
                    )}
                  </div>
                )}

                <dl className="divide-y divide-neutral-100 mb-6">
                  {specs.map((spec) => (
                    <div
                      key={spec.label}
                      className="flex items-center justify-between py-2.5"
                    >
                      <dt className="flex items-center text-sm text-neutral-500">
                        <spec.icon className="w-4 h-4 mr-2 text-primary" />
                        {spec.label}
                      </dt>
                      <dd className="font-semibold text-gray-900">{spec.value}</dd>
                    </div>
                  ))}
                </dl>

                <button
                  onClick={onContact}
                  disabled={apartment.status === 'sold'}
                  className="w-full bg-primary text-white px-6 py-4 rounded-xl hover:bg-primary-dark transition-colors font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {apartment.status === 'sold' ? 'Продаден' : 'Свържете се с нас'}
                </button>

                {apartment.brochureUrl && (
                  <a
                    href={apartment.brochureUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 w-full inline-flex items-center justify-center px-6 py-3 rounded-xl border border-neutral-200 text-primary hover:bg-primary/5 transition-colors font-medium"
                  >
                    <Printer className="w-5 h-5 mr-2" />
                    Брошура (PDF)
                  </a>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default StickyDetailsLayout;
